/* eslint-disable import/first */
import React, { Suspense } from 'react'
import PropTypes from 'prop-types'
import { Route, useNavigate } from 'react-router-dom'
import _ from 'lodash'
import { Views } from 'react-big-calendar'
import moment from 'moment'
import config from '../../config'
import { isWindows, buildPlaceholderEntry, buildCurrentEditableEntry } from '../../utils/appUtils'
import eventUtils from '../../utils/eventUtils'
import timeUtils from '../../utils/timeUtils'
import * as projectUtils from '../../utils/projectUtils'
import { CsvEntryBuilder } from '../../server/utils/csvBuilder'
import { logError, log } from '../../utils/logger'
import {
  deleteUnhandledEntriesForDate,
  getUnhandledEntriesForDate,
  getCsvInfoTotal,
  getProject,
} from '../../App/utils'
import {
  getDurationInHours,
  parseUTCStartDate,
  getUTCStartEndISOForTheDay,
  toEntryDate,
} from '../../server/utils/date'

// Components
import PrivateComponent from '../PrivateRoute'
import SentryRoutes from '../SentryRoutes'
import FeatureGate from '../FeatureGate'
import OfflineModeMessage from '../OfflineModeMessage'
import SessionReconnectionBanner from '../SessionExpiredBanner'

// Modals
import ConfirmationModal from '../ConfirmationModal'
import CsvMultiModal from '../CsvMultiModal'
import IssueReportFromMenu from '../IssueReportFromMenu'
import DelegateAccessFromMenu from '../DelegateAccess/DelegateAccessFromMenu'

// React Query
import queryClient from '../../queryClient'
import {
  TIME_ENTRIES_QUERY_KEY,
  ASSIGNMENTS_QUERY_KEY,
  TIMESHEETS_QUERY_KEY,
  TIME_OFF_ENTRIES_QUERY_KEY,
  OUTLOOK_EVENTS_QUERY_KEY,
  OUTLOOK_EMAILS_QUERY_KEY,
  UNHANDLED_ENTRIES_QUERY_KEY,
  PROJECTS_QUERY_KEY,
  USER_SETTINGS_QUERY_KEY,
} from '../../hooks/queries/keys'
import { mergeByRecency } from '../../hooks/queries/mergeByRecency'

import useGlobalStore from '../../stores/globalStore'

// Entities
import * as userSettingsService from '../../entities/userSettings/service'
import * as timeEntryService from '../../entities/timeEntry/service'
import * as timeOffEntryService from '../../entities/timeOffEntry/service'
import { invalidate as invalidateTimeEntries } from '../../hooks/mutations/timeEntriesCache'

// Update Functions
import { updateUnhandledEntries } from '../DataSync/updateFunc'

// Actions
import * as timesheetAction from '../../server/actions/timesheetAction'

// Constants
import { APPROVED, OPEN } from '../../constants/timesheetStatus'
import { CREATION_WAY, REFERENCE_TYPE } from '../../constants/timeEntry'
import {
  UNASSIGNED_PROJECT,
  UNASSIGNED_PROJECT_REPLICON_ID,
  UNASSIGNED_PROJECT_ID,
} from '../../constants/projects'
import { initialState, initialUserSettings } from '../../App/constants'
import { FUNNEL_ENTRIES_RESOURCE_ID } from '../../constants/events'

import Approvals from '../../views/Approvals/ApprovalsPage'
import ApprovalsIndexRedirect from '../../views/Approvals/ApprovalsIndexRedirect'
import ViewErrorBoundary from '../ViewErrorBoundary'
import ViewLoadingFallback from '../ViewLoadingFallback'

const Auth = React.lazy(() => import('../../views/Auth'))
const ReportsPage = React.lazy(() => import('../../views/reports'))
const Calendar = React.lazy(() => import('../../views/Calendar'))
const Graph = React.lazy(() => import('../../views/graph'))
const Assignments = React.lazy(() => import('../../views/assignments'))
const ErrorScreen = React.lazy(() => import('../../views/ErrorScreen'))
const WorkInProgressPage = React.lazy(() => import('../../views/WorkInProgress/WorkInProgressPage'))
import AdminPage from '../../views/Admin'
import withFeatureFlags, { FeatureFlags } from '../../hocs/withFeatureFlags'
import { validateHexString } from '../../utils/validateHexString'

const { IS_DELEGATE_ENABLED, IS_GRAPH_ENABLED, IS_ASSIGNMENTS_ENABLED } = config
const { DAY: VIEW_PERIOD_DAY, WEEK: VIEW_PERIOD_WEEK } = Views

// Loading component
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px',
      color: '#666',
      width: '100%',
    }}
  >
    Loading...
  </div>
)

class ApplicationClass extends React.Component {
  projectSelectorRef = React.createRef()

  handleModalClose = () => {
    this.props.handleAppState({
      modal: {
        ...this.props.modal,
        open: false,
      },
    })
  }

  renderModalDialog = () => {
    return (
      <ConfirmationModal
        isOpen={this.props.modal.open}
        actionButtons={[
          {
            handler: this.handleModalClose,
            label: 'ok',
          },
        ]}
        title={this.props.modal.title}
        subtitle={this.props.modal.message}
      />
    )
  }

  clodeNoFreeGapModal = () => this.props.handleAppState({ isNoFreeGapModalOpen: false })

  renderNoFreeGapModal = () => {
    return (
      <ConfirmationModal
        isOpen={this.props.isNoFreeGapModalOpen}
        title="This timesheet has exceeded the number of hours allowed for a day. It is not possible to create more time entries at the time."
        titleColor="inherit"
        actionButtons={[
          {
            handler: this.clodeNoFreeGapModal,
            color: 'primary',
            label: 'Ok',
          },
        ]}
      />
    )
  }

  setHasUserGotStarted = async () => {
    this.props.handleAppState({
      hasUserGotStarted: true,
    })
  }

  renderErrorScreen = () => {
    return <ErrorScreen />
  }

  _getTimesheets = () => {
    const delegateId = useGlobalStore.getState().selectedDelegateId ?? null
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: TIMESHEETS_QUERY_KEY })
      .filter(query => (_.last(query.queryKey) ?? null) === delegateId)

    return mergeByRecency(
      queries.map(query => ({ data: query.state.data, updatedAt: query.state.dataUpdatedAt })),
    )
  }

  _getTimeEntries = () => {
    const entries = queryClient.getQueriesData(TIME_ENTRIES_QUERY_KEY)
    return _.flatten(entries.map(([, data]) => data ?? []))
  }

  _getTimeOffEntries = () => {
    const entries = queryClient.getQueriesData(TIME_OFF_ENTRIES_QUERY_KEY)
    return _.flatten(entries.map(([, data]) => data ?? []))
  }

  _getProjects = () => {
    const entries = queryClient.getQueriesData(PROJECTS_QUERY_KEY)
    return _.flatten(entries.map(([, data]) => data ?? []))
  }

  _getExchangeEvents = () => {
    const entries = queryClient.getQueriesData(OUTLOOK_EVENTS_QUERY_KEY)
    return _.flatten(entries.map(([, data]) => data ?? []))
  }

  clearCurrentEditableEntryProject = cb => {
    this.props.handleAppState(
      prevState => ({
        currentEditableEntry: {
          ...prevState.currentEditableEntry,
          originalProjectId: null,
          type: undefined,
        },
      }),
      cb,
    )
  }

  openAddAllEntriesConfirmationModal = (isButton = false) =>
    this.props.handleAppState({
      isAddAllEntriesFromEventsModalOpen: true,
      isAllEventsButton: isButton,
    })

  getSelectedDayTimesheet = () => {
    return eventUtils.getTimesheetForDay(this.props.selectedDate, this._getTimesheets())
  }

  getWeekTimesheets = () => {
    return eventUtils.getWeekTimesheets(this.props.selectedDate, this._getTimesheets())
  }

  areAllTimesheetsSubmitted = () => {
    return _.every(this.getWeekTimesheets(), timesheet => timesheet.approvalStatus === APPROVED)
  }

  handleModalOpen = ({ message, title = '' }) => {
    this.props.handleAppState({
      modal: {
        open: true,
        message,
        title,
      },
    })
  }

  notifyTimesheetActionFailed = () => {
    this.handleModalOpen({
      title: 'SUBMIT FAILED',
      message: 'Your timesheet could not be updated. Please check your connection and try again.',
    })
  }

  readFile = f => {
    return new Promise(resolve => {
      const reader = new FileReader()
      reader.readAsText(f)
      reader.onload = e => resolve(e.target.result)
    })
  }

  handleUpload = async event => {
    try {
      const { files } = event.target
      if (!(files && files.length > 0)) return

      this.props.handleAppState({ csvUploadData: { isCSVUploadInProgress: true } })
      const file = files[0]
      const text = await this.readFile(file)

      const projects = this._getProjects()
      const entryBuilder = new CsvEntryBuilder(text, projects, {
        client: this.props.client,
        selectedDelegateId: this.props.selectedDelegateId,
      })
      const result = await entryBuilder.build()

      if (!result.isOk()) {
        logError(result.getPrettyErrors(), 'handleUploadHandledErrors')
        this.props.handleAppState({ csvUploadData: { isCSVUploadInProgress: false } })
        return
      }

      const csvEntries = []
      const saveFailedEntries = []
      const csvsToUploadTotal = result.entries.length + result.errors.failedEntries.length
      this.props.handleAppState({
        csvUploadData: {
          ...this.props.csvUploadData,
          csvsToUploadTotal,
        },
      })

      for (const [index, entry] of result.entries.entries()) {
        this.props.handleAppState({
          csvUploadData: {
            ...this.props.csvUploadData,
            csvUploadCount: index + 1,
          },
        })
        const csvEntry = await this.handleSafeCreateEntry({
          ...entry,
          creationWay: CREATION_WAY.CSV,
        })
        if (csvEntry) {
          csvEntries.push(csvEntry)
        } else {
          saveFailedEntries.push(entry)
        }
      }

      const uploadResult = {
        successCsvInfoTotal: getCsvInfoTotal(csvEntries),
        failedCsvInfoTotal: getCsvInfoTotal([...result.errors.failedEntries, ...saveFailedEntries]),
        csvUploadData: {
          ...this.props.csvUploadData,
          isCSVUploadInProgress: false,
        },
      }

      this.props.handleAppState(uploadResult)
    } catch (e) {
      this.props.handleAppState({ csvUploadData: { isCSVUploadInProgress: false } })
      logError(e, 'App > handleUpload > catch > e')
    }
  }

  handleReloadApp = async () => {
    window.location.reload()
  }

  isDayViewPeriodSelected = () => {
    return this.props.viewPeriod === VIEW_PERIOD_DAY
  }

  handleUpdateTimesheetsInProgressStatus = isTimesheetLoading => {
    this.props.handleAppState({ isTimesheetLoading })
  }

  focusProjectSelector = () => {
    const projectSelector = _.get(this.projectSelectorRef, 'current', null)
    if (projectSelector) {
      projectSelector.focus()
    }
  }

  handleCalendarSelect = async (selectedDate, cb = () => {}) => {
    const displayingDate = timeUtils.correctTodayHours(selectedDate)
    this.handleUpdateTimesheetsInProgressStatus(true)
    this.props.handleAppState(
      {
        selectedDate: displayingDate,
      },
      async () => {
        this.props.clearCurrentEditableEntry()
        await this.props.updateViewData()
        this.handleUpdateTimesheetsInProgressStatus(false)
        this.focusProjectSelector()
        cb()
      },
    )
  }

  handleFavoriteProjectsSelection = async project => {
    try {
      const projectId = project._id
      const currentSettings =
        queryClient.getQueryData(USER_SETTINGS_QUERY_KEY) ?? initialUserSettings
      const { favoriteProjectIds = [] } = currentSettings
      const doesFavoriteProjectIdExist = _.includes(favoriteProjectIds, projectId)
      const newFavoriteProjectIds = doesFavoriteProjectIdExist
        ? _.filter(favoriteProjectIds, id => id !== projectId)
        : _.concat(favoriteProjectIds, projectId)

      const newUserSettings = { ...currentSettings, favoriteProjectIds: newFavoriteProjectIds }
      await userSettingsService.setUserSettings(this.props.client, newUserSettings)
      await queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY })
    } catch (e) {
      logError(e, 'handleFavoriteProjectsSelection: ')
    }
  }

  setDefaultProject = (userSettings, projectId) => {
    const initialCurrentEditableEntry = this.buildCurrentEditableEntry(
      { ...this.props.initialCurrentEditableEntry, originalProjectId: projectId },
      projectId,
    )
    this.props.handleAppState({
      userSettings,
      initialCurrentEditableEntry,
    })
  }

  handleSetDefaultProject = async projectId => {
    try {
      const currentSettings =
        queryClient.getQueryData(USER_SETTINGS_QUERY_KEY) ?? initialUserSettings
      const newUserSettings = { ...currentSettings, defaultProjectId: projectId }
      const initialCurrentEditableEntry = this.buildCurrentEditableEntry(
        { ...this.props.initialCurrentEditableEntry, originalProjectId: projectId },
        projectId,
      )
      this.props.handleAppState({ initialCurrentEditableEntry })
      await userSettingsService.setUserSettings(this.props.client, newUserSettings)
      await queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY })
    } catch (e) {
      logError(e, 'handleSetDefaultProject: ')
    }
  }

  handleClearDefaultProject = async () => {
    try {
      const currentSettings =
        queryClient.getQueryData(USER_SETTINGS_QUERY_KEY) ?? initialUserSettings
      const newUserSettings = { ...currentSettings, defaultProjectId: null }
      const initialCurrentEditableEntry = this.buildCurrentEditableEntry(
        { ...this.props.initialCurrentEditableEntry, originalProjectId: null },
        null,
      )
      this.props.handleAppState({ initialCurrentEditableEntry })
      await userSettingsService.setUserSettings(this.props.client, newUserSettings)
      await queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY })
    } catch (e) {
      logError(e, 'handleClearDefaultProject: ')
    }
  }

  handleSafeCreateEntry = async entry => {
    const originalEntryDate = toEntryDate(entry.originalEntryDate || entry.start)
    try {
      return await this.props.createEntry({ ...entry, originalEntryDate })
    } catch (e) {
      logError(e, 'handleSafeCreateEntry > e: ')
      this.props.clearCurrentEditableEntry()
      if (entry.creationWay !== CREATION_WAY.CSV) {
        this.handleModalOpen({
          title: 'SAVE FAILED',
          message: 'The time entry could not be saved. Please check your connection and try again.',
        })
      }
      await updateUnhandledEntries({ handleAppState: this.props.handleAppState })
      return undefined
    }
  }

  handleDeleteEntry = async entry => {
    const isValidObjectId = validateHexString(entry._id)
    if (isValidObjectId) {
      await timeEntryService.deleteTimeEntry({
        client: this.props.client,
        timeEntry: entry,
        selectedDelegateId: this.props.selectedDelegateId,
      })
      await invalidateTimeEntries()
    }
    log('handleTimeEntryDelete complete.')
    await this.props.updateViewData()
  }

  handleSafeDeleteEntry = async entry => {
    try {
      await this.handleDeleteEntry(entry)
    } catch (e) {
      logError(e, 'handleSafeDeleteEntry > e:')
    }
  }

  getProjectWithPTOCodes = () => {
    const projects = this._getProjects().filter(f => {
      return !['KS Booked Time Off', 'Not assigned'].includes(f.name)
    })
    const unassignedProject = getProject({
      projects,
      projectId: UNASSIGNED_PROJECT_ID,
      projectRepliconId: UNASSIGNED_PROJECT_REPLICON_ID,
    })

    if (unassignedProject) return projects
    return [...projects, UNASSIGNED_PROJECT]
  }

  buildCurrentEditableEntry = (entry, defaultProjectId) => {
    const projects = this.getProjectWithPTOCodes()
    return buildCurrentEditableEntry({ entry, projects, defaultProjectId })
  }

  _buildCurrentEditableEntry = entryOrOperation => {
    const userSettings = queryClient.getQueryData(USER_SETTINGS_QUERY_KEY) ?? initialUserSettings
    return this.buildCurrentEditableEntry(entryOrOperation, userSettings.defaultProjectId)
  }

  setCurrentEditableEntry = entryOrOperation => {
    const currentEditableEntry = this._buildCurrentEditableEntry(entryOrOperation)
    this.props.handleAppState({ currentEditableEntry })
  }

  addCalendarPlaceholderEvent = (selectedSlot, title = '') => {
    const entry = buildPlaceholderEntry(_.merge({}, selectedSlot, { title }))
    const newEntry = this._buildCurrentEditableEntry(entry)
    this.props.handleAppState({
      currentEditableEntry: newEntry,
    })
  }

  _handleUpdateCurrentEditableEntry = updates => {
    const currentEditableEntry = {
      ...this.props.currentEditableEntry,
      ...updates,
    }
    this.props.handleAppState({ currentEditableEntry })
  }

  _handleSetCurrentEditableEntry = (currentEditableEntry, cb) => {
    this.props.handleAppState(
      {
        currentEditableEntry,
      },
      cb,
    )
  }

  findEntryById = entryToFind => {
    const entries = _.concat(this._getTimeEntries(), this._getTimeOffEntries())
    return _.find(entries, entry => entry._id === entryToFind._id)
  }

  timesheetSubmit = async timesheet => {
    this.props.handleAppState({ isSubmitInProgress: true })
    try {
      await timesheetAction.submitTimesheet({
        client: this.props.client,
        timesheet,
        selectedDelegateId: this.props.selectedDelegateId,
      })
      await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })
    } catch (e) {
      logError(e, 'timesheetSubmit > ')
      this.notifyTimesheetActionFailed()
    }

    this.props.handleAppState({ isSubmitInProgress: false })
  }

  timesheetReopen = async (timesheet, { notifyOnFailure = true } = {}) => {
    this.props.handleAppState({ isSubmitInProgress: true })
    try {
      await timesheetAction.reopenTimesheet({
        client: this.props.client,
        timesheet,
        selectedDelegateId: this.props.selectedDelegateId,
      })
      await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })
    } catch (e) {
      logError(e, 'timesheetReopen > ')
      if (notifyOnFailure) this.notifyTimesheetActionFailed()
    }

    this.props.handleAppState({ isSubmitInProgress: false })
  }

  setTimesheetPending = async timesheet => {
    try {
      await timesheetAction.updateTimesheetApproveState({
        client: this.props.client,
        variables: {
          repliconId: timesheet.repliconId,
          status: 'pending',
        },
      })
      await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })
    } catch (e) {
      logError(e, 'setTimesheetPending > ')
    }
  }

  dayTimesheetSubmission = async timesheet => {
    if (!eventUtils.isTimesheetForDay(timesheet, this.props.selectedDate)) {
      return this.notifyTimesheetActionFailed()
    }
    await this.setTimesheetPending(timesheet)
    if (timesheet.approvalStatus === OPEN) return this.timesheetSubmit(timesheet)
    if (timesheet.approvalStatus === APPROVED) return this.timesheetReopen(timesheet)
  }

  weekTimesheetsSubmission = async (timesheet, areAllTimesheetsSubmitted) => {
    if (areAllTimesheetsSubmitted) {
      await this.timesheetReopen(timesheet)
      return true
    }
    if (timesheet.approvalStatus === APPROVED) return false
    await this.timesheetSubmit(timesheet)
    return true
  }

  _handleTimesheetSubmission = async () => {
    if (this.isDayViewPeriodSelected())
      return this.dayTimesheetSubmission(this.getSelectedDayTimesheet())

    const timesheets = this.getWeekTimesheets()
    const areAllTimesheetsSubmitted = this.areAllTimesheetsSubmitted()

    let hasActionedTimesheet = false
    for (const timesheet of timesheets) {
      const wasActioned = await this.weekTimesheetsSubmission(timesheet, areAllTimesheetsSubmitted)
      hasActionedTimesheet = hasActionedTimesheet || wasActioned
    }

    if (!hasActionedTimesheet) this.notifyTimesheetActionFailed()
  }

  setViewPeriodDay = () =>
    this.props.handleAppState({ viewPeriod: VIEW_PERIOD_DAY }, () => {
      this.props.updateViewData()
      this.props.clearCurrentEditableEntry()
    })

  setViewPeriodWeek = () =>
    this.props.handleAppState({ viewPeriod: VIEW_PERIOD_WEEK }, () => {
      this.props.updateViewData()
      this.props.clearCurrentEditableEntry()
    })

  // MOVE THIS TO SUBMISSION TOOLBAR
  areAllTimesheetsWithEntries = () => {
    const timeEntries = this._getTimeEntries()
    const timeOffEntries = this._getTimeOffEntries()
    const filteredTimesheets = _.filter(this.getWeekTimesheets(), timesheet => {
      const dayName = moment(timesheet.startDate).format('dddd')
      return dayName !== 'Sunday' && dayName !== 'Saturday'
    })
    return _.every(filteredTimesheets, timesheet => {
      const dayEntries = eventUtils.getSelectedDayEntries(timesheet, timeEntries)
      const dayTimeOffEntries = eventUtils.getSelectedDayEntriesByDate(timesheet, timeOffEntries)
      return dayEntries.length > 0 || dayTimeOffEntries.length > 0
    })
  }

  areAllTimesheetsWithoutEntries = () => {
    const timeEntries = this._getTimeEntries()
    const timeOffEntries = this._getTimeOffEntries()
    const filteredTimesheets = _.filter(this.getWeekTimesheets(), timesheet => {
      const dayName = moment(timesheet.startDate).format('dddd')
      return dayName !== 'Sunday' && dayName !== 'Saturday'
    })
    return _.every(filteredTimesheets, timesheet => {
      const dayEntries = eventUtils.getSelectedDayEntries(timesheet, timeEntries)
      const dayTimeOffEntries = eventUtils.getSelectedDayEntriesByDate(timesheet, timeOffEntries)
      return dayEntries.length === 0 && dayTimeOffEntries.length === 0
    })
  }

  handleSwitchIssueReportModalFromMenu = () => {
    this.props.handleAppState(prevState => ({
      isIssueReportModalFromMenuOpen: !prevState.isIssueReportModalFromMenuOpen,
    }))
  }

  handleSwitchDelegateAccessModalFromMenu = () => {
    this.props.handleAppState(prevState => ({
      isDelegateAccessModalFromMenuOpen: !prevState.isDelegateAccessModalFromMenuOpen,
    }))
  }

  openNoFreeGapModal = () => this.props.handleAppState({ isNoFreeGapModalOpen: true })

  handleSetReminderSettings = newSettings => {
    this.props.handleAppState({
      reminderSettings: newSettings,
    })
  }

  selectedDelegateIdHandler = async selectedDelegateId => {
    this.props.handleSelectedDelegateId(selectedDelegateId, true, initialState)

    await queryClient.invalidateQueries({ queryKey: OUTLOOK_EMAILS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: OUTLOOK_EVENTS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: TIME_OFF_ENTRIES_QUERY_KEY })
    queryClient.setQueryData(UNHANDLED_ENTRIES_QUERY_KEY, [])
    await queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_QUERY_KEY })

    this.props.navigate('/')
    this.props.navigate('/', { replace: true })
  }

  renderCalendar = () => {
    return (
      <ViewErrorBoundary>
        <Calendar
          view={this.props.view}
          handleChangeView={newView => this.props.handleAppState({ view: newView })}
          uiRefreshTimePeriod={this.props.getUIRefreshTimePeriod()}
          clearCurrentEditableEntryProject={this.clearCurrentEditableEntryProject}
          handleCreateEntriesForAllEvents={this.openAddAllEntriesConfirmationModal}
          person={this.props.userInfo}
          showModal={this.handleModalOpen}
          handleUpload={this.handleUpload}
          handleReloadApp={this.handleReloadApp}
          isSubmitInProgress={this.props.isSubmitInProgress}
          isTimesheetLoading={this.props.isTimesheetLoading}
          selectedDate={this.props.selectedDate}
          clearCurrentEditableEntry={this.props.clearCurrentEditableEntry}
          handleCalendarSelect={this.handleCalendarSelect}
          handleFavoriteProjectsSelection={this.handleFavoriteProjectsSelection}
          handleSetDefaultProject={this.handleSetDefaultProject}
          handleClearDefaultProject={this.handleClearDefaultProject}
          addCalendarPlaceholderEvent={this.addCalendarPlaceholderEvent}
          currentEditableEntry={this.props.currentEditableEntry}
          handleUpdateCurrentEditableEntry={this._handleUpdateCurrentEditableEntry}
          handleSetCurrentEditableEntry={this._handleSetCurrentEditableEntry}
          findEntryById={this.findEntryById}
          handleTimesheetSubmission={this._handleTimesheetSubmission}
          hasUserGotStarted={this.props.hasUserGotStarted}
          isDayViewPeriodSelected={this.isDayViewPeriodSelected()}
          setViewPeriodDay={this.setViewPeriodDay}
          setViewPeriodWeek={this.setViewPeriodWeek}
          handleSwitchIsIssueReportModalOpen={this.handleSwitchIssueReportModalFromMenu}
          handleSwitchIsDelegateAccessModalOpen={this.handleSwitchDelegateAccessModalFromMenu}
          buildCurrentEditableEntry={this._buildCurrentEditableEntry}
          isConnected={this.props.isConnected}
          needsReauth={this.props.needsReauth}
          handleNoFreeGapModalOpen={this.openNoFreeGapModal}
          handleSetReminderSettings={this.handleSetReminderSettings}
          handleSelectedDelegateId={this.selectedDelegateIdHandler}
          projectSelectorRef={this.projectSelectorRef}
          focusProjectSelector={this.focusProjectSelector}
          setCurrentEditableEntry={this.setCurrentEditableEntry}
          initialCurrentEditableEntry={this.props.initialCurrentEditableEntry}
          updateViewData={this.props.updateViewData}
        />
      </ViewErrorBoundary>
    )
  }

  closeAddAllEntriesFromEventsModal = () =>
    this.props.handleAppState({ isAddAllEntriesFromEventsModalOpen: false })

  buildEntryFromEvent = project => event => {
    const projects = this._getProjects()
    const projectId = event.projectId
    const eventProject = projectId ? getProject({ projects, projectId }) : null

    const projectOrEventProject = _.isNil(eventProject) ? project : eventProject

    const fieldsToInherit = projectUtils.getFieldsToInheritToEntry(projectOrEventProject)
    const billable =
      projectOrEventProject.billable === 'Both' ? 'ProjectRate' : projectOrEventProject.billable

    const newEntry = {
      ...fieldsToInherit,
      start: new Date(event.start),
      end: new Date(event.end),
      originalDurationInHours: getDurationInHours(event.start, event.end),
      resourceId: FUNNEL_ENTRIES_RESOURCE_ID,
      referenceId: event.id,
      referenceType: REFERENCE_TYPE.CALENDAR,
      creationWay: event.creationWay,
      originalComment: event.subject,
      billable,
      sourceId: eventUtils.getEventId(
        _.get(projectOrEventProject, '_id', 'X'),
        event.subject,
        event.start,
        event.end,
      ),
      eventTagId: event.tagId,
    }
    return newEntry
  }

  reportFailedBatch = async (failedCount, totalCount) => {
    if (!failedCount) return
    this.handleModalOpen({
      title: 'SAVE FAILED',
      message: `${failedCount} of ${totalCount} entries could not be saved. Please check your connection and try again.`,
    })
    await updateUnhandledEntries({ handleAppState: this.props.handleAppState })
  }

  createEntriesForAllEventsInADay = async (project, creationWay) => {
    const timeEntries = this._getTimeEntries()
    const exchangeEvents = this._getExchangeEvents()
    const selectedTimesheet = this.getSelectedDayTimesheet()
    if (selectedTimesheet.approvalStatus === APPROVED) return
    const todayEntriesReferenceIds = _.map(
      eventUtils.getSelectedDayEntries(selectedTimesheet, timeEntries),
      entry => entry.referenceId,
    )

    const events = eventUtils.getEventsForDay(new Date(selectedTimesheet.startDate), exchangeEvents)

    const filteredEvents = _.chain(events)
      .filter(event => !_.includes(todayEntriesReferenceIds, event.id) && !event.isAllDay)
      .map(event => ({ ...event, creationWay }))
      .value()

    const entries = filteredEvents.map(this.buildEntryFromEvent(project))
    const { failed } = await this.props.createEntries(entries)
    await this.reportFailedBatch(failed.length, entries.length)
  }

  createEntriesForAllEventsInAWeek = async (project, creationWay) => {
    const timeEntries = this._getTimeEntries()
    const exchangeEvents = this._getExchangeEvents()
    const weekEvents = _.map(this.getWeekTimesheets(), timesheet => {
      if (timesheet.approvalStatus === APPROVED) return []
      const dayEntries = eventUtils.getSelectedDayEntries(timesheet, timeEntries)
      const dayEntriesReferenceIds = _.map(dayEntries, entry => entry.referenceId)
      const events = eventUtils.getEventsForDay(new Date(timesheet.startDate), exchangeEvents)
      return _.filter(
        events,
        event => !_.includes(dayEntriesReferenceIds, event.id) && !event.isAllDay,
      )
    })

    const events = _.chain(weekEvents)
      .flatten()
      .map(event => ({ ...event, creationWay }))
      .value()

    const entries = events.map(this.buildEntryFromEvent(project))
    const { failed } = await this.props.createEntries(entries)
    await this.reportFailedBatch(failed.length, entries.length)
  }

  handleCreateEntriesForAllEvents = async () => {
    const projects = this._getProjects()
    const { isAllEventsButton, view } = this.props
    const userSettings = queryClient.getQueryData(USER_SETTINGS_QUERY_KEY) ?? initialUserSettings
    const { defaultProjectId } = userSettings

    const latestDefaultProjectId = defaultProjectId
    const lastDefaultProject = !_.isNil(latestDefaultProjectId)
      ? getProject({ projects, projectId: latestDefaultProjectId })
      : null

    const projectUsedToCreateAllEventsEntries = !_.isNil(lastDefaultProject)
      ? lastDefaultProject
      : getProject({
          projects,
          projectId: UNASSIGNED_PROJECT_ID,
        })

    const creationWay = isAllEventsButton
      ? CREATION_WAY.TIMELINE_ADD_ALL_BUTTON
      : CREATION_WAY[`${view.toUpperCase()}_ADD_ALL`]

    if (this.isDayViewPeriodSelected()) {
      await this.createEntriesForAllEventsInADay(projectUsedToCreateAllEventsEntries, creationWay)
      return
    }
    await this.createEntriesForAllEventsInAWeek(projectUsedToCreateAllEventsEntries, creationWay)
  }

  handleOkAddAllEntriesFromEventsModal = async () => {
    this.props.handleAppState({ areEventsBeingAdded: true })
    await this.handleCreateEntriesForAllEvents()
    this.closeAddAllEntriesFromEventsModal()
    this.props.handleAppState({ areEventsBeingAdded: false })
  }

  renderAddAllEntriesFromEventsModal = () => {
    return (
      <ConfirmationModal
        isOpen={this.props.isAddAllEntriesFromEventsModalOpen}
        title="Are you sure you want to add all events to your timesheet?"
        actionButtons={[
          {
            handler: this.closeAddAllEntriesFromEventsModal,
            color: 'secondary',
            label: 'Cancel',
            isDisabled: this.props.areEventsBeingAdded,
          },
          {
            handler: this.handleOkAddAllEntriesFromEventsModal,
            color: 'primary',
            label: 'Ok',
            isDisabled: this.props.areEventsBeingAdded,
          },
        ]}
      />
    )
  }

  clearCsvSuccessState = () =>
    this.props.handleAppState({
      successCsvInfoTotal: {},
    })

  clearCsvFailState = () =>
    this.props.handleAppState({
      failedCsvInfoTotal: {},
    })

  handleDismissUnhandledEntries = async dateArr => {
    try {
      await Promise.all(dateArr.map(deleteUnhandledEntriesForDate))
      await updateUnhandledEntries({ handleAppState: this.props.handleAppState })
    } catch (e) {
      logError(e, 'Appjs > handleDismissUnhandledEntries > e: ')
    }
  }

  handleAddUnhandledEntries = async dateStr => {
    const selectedDate = parseUTCStartDate(dateStr)
    try {
      const timesheet = _.find(this._getTimesheets(), t => t.startDate === selectedDate)
      await this.timesheetReopen(timesheet, { notifyOnFailure: false })

      const entries = await getUnhandledEntriesForDate(dateStr)
      for (const entry of entries) {
        await this.handleSafeCreateEntry(entry)
      }

      await deleteUnhandledEntriesForDate(dateStr)
      await updateUnhandledEntries({ handleAppState: this.props.handleAppState })
    } catch (e) {
      logError(e, 'app/index.js > handleAddUnhandledEntries > e:')
    }
  }

  handleAddUnhandledEntriesBulk = async dateArr => {
    this.props.handleAppState({ csvActionButtonsBlocked: true })
    await Promise.all(dateArr.map(this.handleAddUnhandledEntries))
    this.props.handleAppState({ csvActionButtonsBlocked: false })
  }

  handleReplaceUnhandledEntries = async dateStr => {
    const selectedDate = parseUTCStartDate(dateStr)
    try {
      const timesheet = _.find(this._getTimesheets(), t => t.startDate === selectedDate)
      await this.timesheetReopen(timesheet, { notifyOnFailure: false })
      const { startISO, endISO } = getUTCStartEndISOForTheDay(dateStr)

      const localEntries = await timeEntryService.fetchTimeEntriesInDateRange({
        client: this.props.client,
        startISO,
        endISO,
        selectedDelegateId: this.props.selectedDelegateId,
      })
      const localPTOEntries = await timeOffEntryService.fetchTimeOffEntriesInDateRange({
        client: this.props.client,
        startISO,
        endISO,
        selectedDelegateId: this.props.selectedDelegateId,
      })

      for (const entry of localEntries) {
        await this.handleSafeDeleteEntry(entry)
      }
      for (const ptoEntry of localPTOEntries) {
        await this.handleSafeDeleteEntry(ptoEntry)
      }
      const entries = await getUnhandledEntriesForDate(dateStr)
      for (const entry of entries) {
        await this.handleSafeCreateEntry(entry)
      }
      await deleteUnhandledEntriesForDate(dateStr)
      await updateUnhandledEntries({ handleAppState: this.props.handleAppState })
    } catch (e) {
      logError(e, 'app/index.js > handleReplaceUnhandledEntries > e: ')
    }
  }

  handleReplaceUnhandledEntriesBulk = async dateArr => {
    this.props.handleAppState({ csvActionButtonsBlocked: true })
    await Promise.all(dateArr.map(this.handleReplaceUnhandledEntries))
    this.props.handleAppState({ csvActionButtonsBlocked: false })
  }

  renderCsvMultiModal = () => {
    return (
      <CsvMultiModal
        successCsvInfoTotal={this.props.successCsvInfoTotal}
        failedCsvInfoTotal={this.props.failedCsvInfoTotal}
        clearCsvSuccessState={this.clearCsvSuccessState}
        clearCsvFailState={this.clearCsvFailState}
        unhandledEntriesInfo={this.props.unhandledEntriesInfo}
        onDismiss={this.handleDismissUnhandledEntries}
        onAdd={this.handleAddUnhandledEntriesBulk}
        onReplace={this.handleReplaceUnhandledEntriesBulk}
        csvActionButtonsBlocked={this.props.csvActionButtonsBlocked}
        csvUploadData={this.props.csvUploadData}
      />
    )
  }

  renderIssueReportFromMenu = () => {
    return (
      <IssueReportFromMenu
        client={this.props.client}
        isOpen={this.props.isIssueReportModalFromMenuOpen}
        handleSwitchIsIssueReportModalOpen={this.handleSwitchIssueReportModalFromMenu}
        viewPeriod={this.props.viewPeriod}
        selectedDate={this.props.selectedDate}
      />
    )
  }

  renderDelegateAccessFromMenu = () => {
    return (
      <DelegateAccessFromMenu
        isOpen={this.props.isDelegateAccessModalFromMenuOpen}
        handleSwitchIsDelegateAccessModalOpen={this.handleSwitchDelegateAccessModalFromMenu}
      />
    )
  }

  renderAssignments = () => {
    return (
      <ViewErrorBoundary>
        <Suspense fallback={<ViewLoadingFallback />}>
          <Assignments />
        </Suspense>
      </ViewErrorBoundary>
    )
  }

  render() {
    const { hasUserGotStarted, userInfo } = this.props
    // if user's position or user's employeeType are Contractor - Expert, do not show assignments, reports and graph sections
    const isExpert =
      _.get(userInfo, 'position') === 'Contractor - Expert' ||
      _.get(userInfo, 'employeeType', '').includes('Contractor - Expert')

    const showsBanner =
      this.props.isUserLoggedIn && (!this.props.isConnected || this.props.needsReauth)

    return (
      <div data-testid="app" className={`app-window ${isWindows() ? 'windows' : ''}`}>
        {!this.props.isConnected && this.props.isUserLoggedIn && <OfflineModeMessage />}
        {this.props.isConnected && this.props.needsReauth && this.props.isUserLoggedIn && (
          <SessionReconnectionBanner />
        )}
        {this.renderModalDialog()}
        {this.renderNoFreeGapModal()}
        <div
          className="flex-column"
          style={showsBanner ? { height: `calc(100vh - 51px)` } : { height: '100vh' }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <SentryRoutes>
              <Route path="/auth" element={<Auth hasUserGotStarted={hasUserGotStarted} />} />

              <Route
                path="/error"
                element={<PrivateComponent>{this.renderErrorScreen()}</PrivateComponent>}
              />
              <Route
                path="/"
                element={<PrivateComponent>{this.renderCalendar()}</PrivateComponent>}
              />
              <Route element={<FeatureGate flag={FeatureFlags.ENABLE_PROJECT_VIEW_DASHBOARD} />}>
                <Route path="/lead-view/approvals">
                  <Route index element={<ApprovalsIndexRedirect />} />
                  <Route
                    path=":tab"
                    element={
                      <PrivateComponent>
                        <ViewErrorBoundary>
                          <Suspense fallback={<ViewLoadingFallback />}>
                            <Approvals />
                          </Suspense>
                        </ViewErrorBoundary>
                      </PrivateComponent>
                    }
                  />
                </Route>
                <Route
                  path="/lead-view/work-in-progress"
                  element={
                    <PrivateComponent>
                      <WorkInProgressPage person={userInfo} client={this.props.client} />
                    </PrivateComponent>
                  }
                />
              </Route>

              <Route element={<FeatureGate flag={FeatureFlags.VIEW_ADMIN_PAGE_GLIMPSE} />}>
                <Route
                  path="/admin/*"
                  element={
                    <PrivateComponent>
                      <AdminPage />
                    </PrivateComponent>
                  }
                />
              </Route>

              {!isExpert && (
                <Route
                  path="/reports"
                  element={
                    <PrivateComponent>
                      <ReportsPage />
                    </PrivateComponent>
                  }
                />
              )}
              {IS_GRAPH_ENABLED && !isExpert && (
                <Route
                  path="/graph"
                  element={
                    <PrivateComponent>
                      <Graph />
                    </PrivateComponent>
                  }
                />
              )}
              {IS_ASSIGNMENTS_ENABLED && !isExpert && (
                <>
                  <Route
                    path="/assignments"
                    element={<PrivateComponent>{this.renderAssignments()}</PrivateComponent>}
                  />
                  <Route
                    path="/assignments/:view"
                    element={<PrivateComponent>{this.renderAssignments()}</PrivateComponent>}
                  />
                  <Route
                    path="/assignments/:view/:projectId"
                    element={<PrivateComponent>{this.renderAssignments()}</PrivateComponent>}
                  />
                </>
              )}
            </SentryRoutes>
          </Suspense>
          {this.renderAddAllEntriesFromEventsModal()}
        </div>
        {this.renderCsvMultiModal()}
        {this.renderIssueReportFromMenu()}
        {IS_DELEGATE_ENABLED && this.renderDelegateAccessFromMenu()}
      </div>
    )
  }
}

ApplicationClass.propTypes = {
  isUserLoggedIn: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  isConnected: PropTypes.bool.isRequired,
  needsReauth: PropTypes.bool.isRequired,
  client: PropTypes.object.isRequired,
  handleAppState: PropTypes.func.isRequired,
  getUIRefreshTimePeriod: PropTypes.func.isRequired,
  updateViewData: PropTypes.func.isRequired,
  updateView: PropTypes.func.isRequired,
  modal: PropTypes.object,
  isNoFreeGapModalOpen: PropTypes.bool,
  selectedDate: PropTypes.any,
  csvUploadData: PropTypes.object,
  viewPeriod: PropTypes.string,
  selectedDelegateId: PropTypes.string,
  currentEditableEntry: PropTypes.object,
  view: PropTypes.string,
  isSubmitInProgress: PropTypes.bool,
  isTimesheetLoading: PropTypes.bool,
  isAllEventsButton: PropTypes.bool,
  isAddAllEntriesFromEventsModalOpen: PropTypes.bool,
  areEventsBeingAdded: PropTypes.bool,
  successCsvInfoTotal: PropTypes.object,
  failedCsvInfoTotal: PropTypes.object,
  csvActionButtonsBlocked: PropTypes.bool,
  isIssueReportModalFromMenuOpen: PropTypes.bool,
  isDelegateAccessModalFromMenuOpen: PropTypes.bool,
  hasUserGotStarted: PropTypes.bool,
  clearCurrentEditableEntry: PropTypes.func,
  setInitialCurrentEditableEntry: PropTypes.func,
  initialCurrentEditableEntry: PropTypes.object,
  handleSelectedDelegateId: PropTypes.func,
  navigate: PropTypes.func.isRequired,
  createEntry: PropTypes.func.isRequired,
  createEntries: PropTypes.func.isRequired,
}

const Application = props => {
  const navigate = useNavigate()
  return <ApplicationClass {...props} navigate={navigate} />
}

export { ApplicationClass }
export default withFeatureFlags(Application)
